import { Component, OnInit, SecurityContext } from '@angular/core';
import { AppContextService } from '../../../services/app-context.service';
import { ReportingApiService } from '../../../server-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { LogService } from '../../../services/log/log.service';
import { OperationError } from '../../../models/server-result/operation-error';
import { User } from '../../../models/User/user';
import { OperationResult } from '../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { VerticalBarChart } from '../../../models/charts/vertical-bar-chart';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { DocumentForecastByState } from '../../../models/dashboard/document-forecast-by-paymentstate';
import { FactoringContractsSearchCriteria } from '../../../models/contracts/factoring/factoring-contracts-search-criteria';
import { FactoringContract } from '../../../models/contracts/factoring/factoring-contract';
import { ConfirmingConnectionsSearchCriteria } from '../../../models/contracts/confirming/confirming-connections-search-criteria';
import { ConfirmingConnection } from '../../../models/contracts/confirming/confirming-connection';
import { CurrencyCode } from '../../../models/enums/currency-code';
import { DomSanitizer } from '@angular/platform-browser';
import { PendingApprovalDocumentsAmountDto } from '../../../models/dashboard/pending-approval-documents-amount-dto';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';

type DocumentForecastSeriesData = {
	PaymentState: string;
	TotalAmount: string;
};

type SeriesDetail = {
	name: string;
	value: string;
};

type SeriesData = {
	name: string;
	series: SeriesDetail[];
};

type CurrencyViewModel = {
	Currency: string;
	Selected: boolean;
};

@Component({
	selector: 'app-dashboard-supplier',
	templateUrl: './dashboard-supplier.component.html',
	styleUrls: ['../private-home.component.scss', './dashboard-supplier.component.scss'],
	standalone: false,
})
export class DashboardSupplierComponent implements OnInit {
	public loggedUser: User | null = new User();
	public requestErrors: OperationError[] = [];

	public CurrentMonth: Date;
	public notPaidDocuments: number = 0;

	// Confirming properties
	public confirmingApprovedRequestFunds: number = 0;
	public currentFundingRequestsAmount: number = 0;
	public suppliersActiveConnections: number = 0;

	// Factoring properties
	public factoringBuyersActiveConnections: number = 0;
	public factoringAdvanceableAmount: number = 0;
	public factoringAdvancedAmount: number = 0;
	public factoringPortfolioAmount: number = 0;
	public factoringPendingApprovalDocumentsAmount: number = 0;

	public selectedCurrency: string = CurrencyCode.Euro;

	public documentForecastChart: VerticalBarChart = new VerticalBarChart();

	// Tab management
	public confirmingTabVisible: boolean = false;
	public factoringTabVisible: boolean = false;
	public confirmingTabSelected: boolean = false;
	public factoringTabSelected: boolean = false;

	public orderedCurrencies: CurrencyViewModel[] = [];

	protected FileStatus = FileStatus;

	// Loading states for dashboard cards
	public loadingFactoringPortfolioAmount: boolean = false;
	public loadingFactoringAdvancedAmount: boolean = false;
	public loadingFactoringAdvanceableAmount: boolean = false;
	public loadingFactoringBuyersActiveConnections: boolean = false;
	public loadingFactoringPendingApprovalDocumentsAmount: boolean = false;
	public loadingNotPaidDocuments: boolean = false;

	// Confirming loading states
	public loadingConfirmingApprovedRequestFunds: boolean = false;
	public loadingCurrentFundingRequestsAmount: boolean = false;
	public loadingSuppliersActiveConnections: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private logger: LogService,
		private dateLocale: DateLocalePipe,
		private domSanitizer: DomSanitizer,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			this.CurrentMonth = this.dateLocale.transform(new Date(), 'MM/yyyy');

			// Set tab visibility based on user permissions
			if (this.loggedUser?.canSeeConfirmingOptions()) {
				this.confirmingTabVisible = true;
				this.confirmingTabSelected = true;
				this.clickedConfirmingTab();
			}

			if (this.loggedUser?.canSeeFactoringOptions()) {
				this.factoringTabVisible = true;
				this.factoringTabSelected = !this.confirmingTabSelected;

				if (this.factoringTabSelected) this.clickedFactoringTab();
			}

			// Initialize chart configuration
			this.documentForecastChart.View = [];
			this.documentForecastChart.ShowXAxis = true;
			this.documentForecastChart.ShowLegend = false;
			this.documentForecastChart.LegendTitle = '';
			this.documentForecastChart.ShowYAxis = true;
			this.documentForecastChart.ShowXAxisLabel = true;
			this.documentForecastChart.XAxisLabel = this.translateService.translate('dashboard-buyer.chart.buyerConfirmedPaymentDate');
			this.documentForecastChart.ShowYAxisLabel = true;
			this.documentForecastChart.YAxisLabel = this.selectedCurrency;
			this.documentForecastChart.ColorScheme = {
				domain: ['#3949ab', '#ff6d6e', '#40c9c5', '#f6c951', '#48aeff', '#6E47C0', '#88D4FF', '#C392FF'],
			};
		});
	}

	private getNotPaidDocumentsSum(currency: string): void {
		this.loadingNotPaidDocuments = true;
		this.notPaidDocuments = 0;
		this.reportingApiService.getNotPaidDocumentsSum(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingNotPaidDocuments = false;
				opResult.IsValid
					? (this.notPaidDocuments = opResult.Data)
					: this.logger.error(
							this.domSanitizer.sanitize(
								SecurityContext.HTML,
								'[DashboardSupplierComponent] Invalid result from getNotPaidDocumentsSum()'
							)!,
							opResult
						);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingNotPaidDocuments = false;
				this.logger.error(
					'[DashboardSupplierComponent] Invalid result from getNotPaidDocumentsSum()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getConfirmingSuppliersActiveConnections(currency: string): void {
		this.loadingSuppliersActiveConnections = true;
		this.suppliersActiveConnections = 0;
		this.reportingApiService.getConfirmingSuppliersActiveConnections(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingSuppliersActiveConnections = false;
				opResult.IsValid
					? (this.suppliersActiveConnections = opResult.Data)
					: this.logger.error(
							this.domSanitizer.sanitize(
								SecurityContext.HTML,
								'[DashboardSupplierComponent] Invalid result from getConfirmingSuppliersActiveConnections()'
							)!,
							opResult
						);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingSuppliersActiveConnections = false;
				this.logger.error(
					'[DashboardSupplierComponent] Invalid result from getConfirmingSuppliersActiveConnections()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getConfirmingCurrentFundingRequestsAmount(currency: string): void {
		this.loadingCurrentFundingRequestsAmount = true;
		this.currentFundingRequestsAmount = 0;
		this.reportingApiService.getConfirmingCurrentFundingRequestsAmount(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingCurrentFundingRequestsAmount = false;
				opResult.IsValid
					? (this.currentFundingRequestsAmount = opResult.Data)
					: this.logger.error(
							this.domSanitizer.sanitize(
								SecurityContext.HTML,
								'[DashboardSupplierComponent] Invalid result from getConfirmingCurrentFundingRequestsAmount()'
							)!,
							opResult
						);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingCurrentFundingRequestsAmount = false;
				this.logger.error(
					'[DashboardSupplierComponent] Invalid result from getConfirmingCurrentFundingRequestsAmount()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getConfirmingApprovedRequestFunds(currency: string) {
		this.loadingConfirmingApprovedRequestFunds = true;
		this.confirmingApprovedRequestFunds = 0;
		const fundingRequestDocumentStates = ['Approved'];

		this.reportingApiService.getConfirmingFundingRequestsTotalAdvancedAmount(fundingRequestDocumentStates, currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingConfirmingApprovedRequestFunds = false;
				opResult.IsValid
					? (this.confirmingApprovedRequestFunds = opResult.Data)
					: this.logger.error(
							this.domSanitizer.sanitize(
								SecurityContext.HTML,
								'[DashboardSupplierComponent] Invalid result from getConfirmingFundingRequestsTotalAdvancedAmount'
							)!,
							opResult
						);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingConfirmingApprovedRequestFunds = false;
				this.logger.error(
					'[DashboardSupplierComponent] HttpError result from getConfirmingFundingRequestsTotalAdvancedAmount',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getDocumentForecast(currency: string) {
		// Reset chart data before fetching new data
		this.documentForecastChart.Results = [];

		this.reportingApiService.getDailyDocumentForecast(currency).subscribe({
			next: (opResult: OperationResult<DocumentForecastByState[]>) => {
				if (opResult.IsValid) {
					let documentForecastByPaymentDate: { [paymentDate: string]: DocumentForecastSeriesData[] } = {};

					this.documentForecastChart.ShowLegend = opResult.Data.some((d) => !!d.DocumentPaymentState);

					for (let index = 0; index < opResult.Data.length; index++) {
						const element = opResult.Data[index];

						let key = this.dateLocale.transform(element.BuyerPaymentDate);
						let dictBuyerPaymentDate = documentForecastByPaymentDate[key];

						if (!dictBuyerPaymentDate) {
							dictBuyerPaymentDate = [];
						}

						var stateToDecode = element.DocumentPaymentState;
						var dicPaymentStateEntry = !!stateToDecode ? this.translateService.translate(stateToDecode, 'list-paymentState') : null;

						if (!!stateToDecode && !!dicPaymentStateEntry) {
							dictBuyerPaymentDate.push(<DocumentForecastSeriesData>{
								PaymentState: dicPaymentStateEntry,
								TotalAmount: element.TotalAmount,
							});
						}
						documentForecastByPaymentDate[key] = dictBuyerPaymentDate;
					}

					let results: SeriesData[] = [];

					Object.keys(documentForecastByPaymentDate).forEach(function (key) {
						results.push({
							name: key,
							series: documentForecastByPaymentDate[key].map(
								(r) =>
									<SeriesDetail>{
										name: r.PaymentState,
										value: r.TotalAmount,
									}
							),
						});
					});

					this.documentForecastChart.Results = results;
				} else {
					this.logger.error(
						this.domSanitizer.sanitize(SecurityContext.URL, '[DashboardSupplierComponent] Invalid result from getDocumentForecast')!,
						opResult
					);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.logger.error(
					'[DashboardSupplierComponent] HttpError result from getDocumentForecast',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.loadingFactoringPendingApprovalDocumentsAmount = true;
		this.factoringPendingApprovalDocumentsAmount = 0;
		this.reportingApiService.getPendingApprovalDocumentsAmount(currency).subscribe({
			next: (opResult: OperationResult<PendingApprovalDocumentsAmountDto>) => {
				this.loadingFactoringPendingApprovalDocumentsAmount = false;
				if (opResult.IsValid) {
					this.factoringPendingApprovalDocumentsAmount = opResult.Data.Factoring;
				} else {
					this.logger.error('[DashboardSupplierComponent] Invalid result from getPendingApprovalDocumentsAmount', opResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringPendingApprovalDocumentsAmount = false;
				this.logger.error(
					'[DashboardSupplierComponent] HttpError result from getPendingApprovalDocumentsAmount',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getFactoringContracts() {
		// Set loading states for values that are calculated in this method
		this.loadingFactoringPortfolioAmount = true;
		this.loadingFactoringAdvancedAmount = true;
		this.loadingFactoringAdvanceableAmount = true;

		const criteria: FactoringContractsSearchCriteria = new FactoringContractsSearchCriteria();
		criteria.OrderBy = 'SupplierName';
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new FactoringContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.SupplierIdList = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];

		this.serverApiService.searchFactoringContracts(criteria).subscribe({
			next: (operationResult: OperationResult<FactoringContract[]>) => {
				if (operationResult.IsValid) {
					if (!!operationResult.Data && operationResult.Data.length > 0) {
						operationResult.Data.forEach((contract) => {
							this.factoringPortfolioAmount += contract.DocumentPortfolioAmount;
							this.factoringAdvanceableAmount += contract.AdvanceableAmount;
							this.factoringAdvancedAmount += contract.AdvancedAmount;
						});
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}

				// Set loading states to false after all calculations are complete
				this.loadingFactoringPortfolioAmount = false;
				this.loadingFactoringAdvancedAmount = false;
				this.loadingFactoringAdvanceableAmount = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

				// Set loading states to false on error
				this.loadingFactoringPortfolioAmount = false;
				this.loadingFactoringAdvancedAmount = false;
				this.loadingFactoringAdvanceableAmount = false;
			},
		});
	}

	private getFactoringBuyersActiveConnections(): void {
		this.loadingFactoringBuyersActiveConnections = true;
		this.factoringBuyersActiveConnections = 0;
		this.reportingApiService.getFactoringBuyersActiveConnections().subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingFactoringBuyersActiveConnections = false;
				opResult.IsValid
					? (this.factoringBuyersActiveConnections = opResult.Data)
					: this.logger.error('[DashboardSupplierComponent] Invalid result from getFactoringBuyersActiveConnections()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringBuyersActiveConnections = false;
				this.logger.error(
					'[DashboardSupplierComponent] Invalid result from getFactoringBuyersActiveConnections() ',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private loadAllFactoringData(): void {
		this.resetValues();

		this.getNotPaidDocumentsSum(CurrencyCode.Euro);
		this.getDocumentForecast(CurrencyCode.Euro);
		this.getPendingApprovalDocumentsAmount(CurrencyCode.Euro);

		this.getFactoringBuyersActiveConnections();
		this.getFactoringContracts();
	}

	private loadAllConfirmingData(): void {
		this.resetValues();

		const criteria: ConfirmingConnectionsSearchCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.RestrictionCriteria.ConnectionStateList = ['Active', 'Inactive'];
		criteria.RestrictionCriteria.ContractStateList = ['Active', 'Inactive'];

		this.serverApiService.searchConfirmingConnections(criteria).subscribe({
			next: (operationResult: OperationResult<ConfirmingConnection[]>) => {
				if (operationResult.IsValid) {
					if (!!operationResult.Data && operationResult.Data.length > 0) {
						let currencies: CurrencyViewModel[] = [];

						for (let index = 0; index < operationResult.Data.length; index++) {
							let connection: ConfirmingConnection = operationResult.Data[index];

							if (currencies.findIndex((c) => c.Currency == connection.Currency) == -1)
								currencies.push(<CurrencyViewModel>{
									Currency: connection.Currency,
									Selected: false,
								});
						}

						this.orderedCurrencies = currencies.sort(function (c1: CurrencyViewModel, c2: CurrencyViewModel) {
							var currency1 = c1.Currency.toUpperCase();
							var currency2 = c2.Currency.toUpperCase();
							return currency1 < currency2 ? -1 : currency1 > currency2 ? 1 : 0;
						});

						if (!!this.orderedCurrencies && this.orderedCurrencies.length > 0) {
							let initialCurrencytoProcess = this.orderedCurrencies[0].Currency;
							this.orderedCurrencies[0].Selected = true;
							this.clickedCurrency(initialCurrencytoProcess);
							this.getDocumentForecast(initialCurrencytoProcess);
						}
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	public clickedConfirmingTab(): void {
		this.confirmingTabSelected = true;
		this.factoringTabSelected = false;
		this.loadAllConfirmingData();
	}

	public clickedFactoringTab(): void {
		this.confirmingTabSelected = false;
		this.factoringTabSelected = true;
		this.selectedCurrency = CurrencyCode.Euro;
		this.loadAllFactoringData();
	}

	public clickedCurrency(currency: string): void {
		this.resetValues();

		this.selectedCurrency = currency;
		this.documentForecastChart.YAxisLabel = this.selectedCurrency;

		this.orderedCurrencies.forEach((orderedCurrency) => {
			orderedCurrency.Selected = orderedCurrency.Currency == currency;
		});

		this.getNotPaidDocumentsSum(currency);
		this.getDocumentForecast(currency);
		this.getPendingApprovalDocumentsAmount(currency);

		this.getConfirmingCurrentFundingRequestsAmount(currency);
		this.getConfirmingApprovedRequestFunds(currency);
		this.getConfirmingSuppliersActiveConnections(currency);
	}

	private resetValues() {
		this.notPaidDocuments = 0;

		// Reset confirming values
		this.confirmingApprovedRequestFunds = 0;
		this.currentFundingRequestsAmount = 0;
		this.suppliersActiveConnections = 0;

		// Reset factoring values
		this.factoringPortfolioAmount = 0;
		this.factoringAdvancedAmount = 0;
		this.factoringAdvanceableAmount = 0;
		this.factoringBuyersActiveConnections = 0;
		this.factoringPendingApprovalDocumentsAmount = 0;

		// Reset chart data
		this.documentForecastChart.Results = [];
	}

	public notMultipleCurrencies(currencies: CurrencyViewModel[]): boolean {
		return currencies?.length <= 1;
	}

	public navigateToFilesIntegration(key: string): void {
		SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
	}

	public refreshAllData(): void {
		if (this.confirmingTabSelected) {
			this.loadAllConfirmingData();
		} else if (this.factoringTabSelected) {
			this.loadAllFactoringData();
		}
	}
}
